import { ArrayUtils } from '@shopping/common'
import { axios } from 'common/utils'
import { DisplayType, MenuState } from 'common/ducks/menu'
import { getSubVerticalPath, SubVertical, subVerticalConfig, Vertical } from 'common/constants'
import { DisplayMenu, Menu, MenuLayer, ROOT_ID } from 'common/types/entities/mongo/menu'
import { isMobileEnv } from 'common/utils/common'
import { ServiceMenu } from 'common/types/entities/api/menu'
import { MenuType, ProductMenuAlias } from 'common/constants/menu'
import hostConfig from 'common/config'

export const fetchMenusOfChannel = async (
  channelId: string,
  parentId: string = ROOT_ID,
  displayType: DisplayType.STORE_HOME | DisplayType.NO_RULE,
  subVertical?: SubVertical,
): Promise<DisplayMenu[]> => {
  const result = await axios.get<DisplayMenu[]>(`/v1/channels/${channelId}/menus`, {
    params: { parentId, displayType, subVertical },
  })
  return result && (result.data || [])
}

export const fetchLayersOfChannel = async (
  channelId: string,
  menuId: string,
  displayType: DisplayType = DisplayType.STORE_HOME,
  subVertical: SubVertical,
): Promise<MenuLayer[]> => {
  const result = await axios.get<MenuLayer[]>(`/v1/channels/${channelId}/menus/${menuId}/layers`, {
    params: { displayType, subVertical },
  })
  return result && (result.data || [])
}

export const fetchMenusOfBranch = async (
  storeCategoryId: string,
  parentId: string = ROOT_ID,
): Promise<DisplayMenu[]> => {
  const result = await axios.get<DisplayMenu[]>(`/v1/store-categories/${storeCategoryId}/menus`, {
    params: { parentId },
  })
  return result && (result.data || [])
}

export const fetchLayersOfBranch = async (storeCategoryId: string, menuId: string): Promise<MenuLayer[]> => {
  const result = await axios.get<MenuLayer[]>(`/v1/store-categories/${storeCategoryId}/menus/${menuId}/layers`)
  return result && (result.data || [])
}

export const fetchBy = async (
  subVertical: SubVertical,
  parentId: string,
  displayType: DisplayType,
  menuTypes?: MenuType[],
): Promise<DisplayMenu[]> => {
  return await axios
    .get<DisplayMenu[]>(`/v1/menus`, {
      params: {
        subVertical,
        parentId,
        displayType,
        menuTypes,
      },
    })
    .then(result => (result && result.data) || [])
}

export const fetchLayers = async (menuId: string, displayType: DisplayType): Promise<MenuLayer[]> => {
  const result = await axios.get<MenuLayer[]>(`/v1/menus/${menuId}/layers`, { params: { displayType } })
  return result && (result.data || [])
}

export const fetchMenusThatHasProducts = async (
  subVertical: SubVertical,
  parentId: string,
  minimumProductSize: number,
): Promise<DisplayMenu[]> => {
  return await axios
    .get<DisplayMenu[]>('/v1/menus/has-displayable-products', { params: { subVertical, parentId, minimumProductSize } })
    .then(result => (result && result.data) || [])
}

export const getMenusByMenuIds = async (menuIds: string[]): Promise<Menu[]> => {
  const params = {
    menuIds,
  }
  const response = await axios.get<Menu[]>(`/v1/menus/all`, {
    params,
  })
  return response && response.data
}

export const getRepresentativeMenuId = (menus: Menu[]): string => {
  const luxuryMenu = menus.find(menu => menu.alias === 'LUXURY')
  if (luxuryMenu) return luxuryMenu._id

  const rootParentId = '0'
  const parentMenuIds = [rootParentId, ...menus.map(menu => menu._id)]
  const allParentMenuIds = menus.map(menu => menu.parentId)

  const representativeMenuIds = parentMenuIds.filter(parentMenuId => !allParentMenuIds.includes(parentMenuId))

  if (representativeMenuIds && representativeMenuIds.length === 1) return representativeMenuIds[0]

  return menus[menus.length - 1]._id
}

export const getPanelUrlPath = (subVertical: SubVertical, menus: Menu[]): string => {
  if (isMobileEnv || subVertical !== SubVertical.STYLE) return 'category'

  const handmadeShoesMenu = menus.find(isHandmadeShoes)
  if (handmadeShoesMenu) {
    return 'handmade-shoes'
  }

  return 'category'
}

export const isLinkTypeMenu = (menu: DisplayMenu): boolean => menu && menu.type === MenuType.LINK

export const getSelectedMenu = (menuLayer: MenuLayer): DisplayMenu => {
  return menuLayer && menuLayer.displayMenus && menuLayer.displayMenus.find(menu => menu.id === menuLayer.selectedId)
}

export const getSelectedMenuIndex = (menuLayer: MenuLayer): number => {
  return (
    menuLayer && menuLayer.displayMenus && menuLayer.displayMenus.findIndex(menu => menu.id === menuLayer.selectedId)
  )
}

export const fetchMenusLinkByVertical = async (vertical: Vertical): Promise<ServiceMenu> => {
  const result = await axios.get<ServiceMenu>(`/v1/menus/verticals/${vertical}`)
  return result && result.data
}

const isHandmadeShoes = (menu: DisplayMenu | Menu) =>
  menu && menu.alias && menu.alias.toLocaleLowerCase() === 'handmadeshoes'

export const getCategoryListingUrl = (subVertical: SubVertical, menu: DisplayMenu) => {
  if (!menu || !menu.id) return '#'

  if (subVertical === SubVertical.STYLE && isHandmadeShoes(menu)) {
    return `${getSubVerticalPath(subVertical)}/handmade-shoes?menu=${menu.id}`
  }
  return getSimpleCategoryListingUrl(subVertical, menu.id)
}

export const getDefaultCategoryUrl = subVertical => `${getSubVerticalPath(subVertical)}/category`

export const getSimpleCategoryListingUrl = (subVertical, menuId) =>
  `${getSubVerticalPath(subVertical)}/category?menu=${menuId}`

export const isThemeMenuOfLiving = (menuState: MenuState, menuId: string) => {
  if (!menuId) return false

  if (subVerticalConfig[menuState.subVertical].vertical !== Vertical.LIVING) return false

  const menu = menuState.first.displayMenus.find(menu => menu.id === menuId)

  if (!menu || !menu.alias || !menu.alias.endsWith('_theme')) return false

  return true
}

export const isIncludesMenu = (menuLayer: MenuLayer, menuId: string) => {
  return menuId && menuLayer && menuLayer.displayMenus && menuLayer.displayMenus.findIndex(menu => menu.id === menuId)
}

export const clickFirstMenuOf2ndDepth = (subVertical, menuId, clickHandler) =>
  fetchBy(subVertical, menuId, DisplayType.CATEGORY_HOME)
    .then(menus => menus[1].id)
    .then(id => clickHandler(id)())

export const getSelectiveUrl = () => {
  return `${hostConfig.selective}/discover?shp`
}

export const getSelectedId = (menuState: MenuState) => {
  if (menuState.third.selectedId) return menuState.third.selectedId
  if (menuState.second.selectedId) return menuState.second.selectedId
  if (menuState.first.selectedId) return menuState.first.selectedId
}

export const getSelectedMenuForAlias = (menuState: MenuState, alias: string) => {
  if (menuState.third.displayMenus) {
    return menuState.third.displayMenus.find(menu => menu.alias && menu.alias === alias)
  }
  if (menuState.second.displayMenus) {
    return menuState.second.displayMenus.find(menu => menu.alias && menu.alias === alias)
  }
  if (menuState.first.displayMenus) {
    return menuState.first.displayMenus.find(menu => menu.alias && menu.alias === alias)
  }
}

export const getFirstMenuId = (menuState: MenuState, subVertical: SubVertical) => {
  const firstMenu =
    menuState.first &&
    menuState.first.displayMenus &&
    menuState.first.displayMenus.find(menu => menu.alias && menu.alias === ProductMenuAlias[subVertical])
  return firstMenu && firstMenu.id
}

export const fetchWithFilter = async (
  subVertical: SubVertical,
  menuId: string,
  displayType: DisplayType,
): Promise<MenuLayer[]> => {
  const result = await axios.get<MenuLayer[]>(`/v1/menus/with-filter`, {
    params: { subVertical, menuId, displayType },
  })
  return result && (result.data || [])
}

// 하위 상품 카테고리를 하나의 배열로 리턴한다
export const getAllProductCategoriesOfSelectedMenu = (menus: MenuState) => {
  const lastDepthMenu = menus.third || menus.second || menus.first
  const menuIndex = getSelectedMenuIndex(lastDepthMenu)
  let categories = []

  if (!lastDepthMenu || ArrayUtils.isEmpty(lastDepthMenu.displayMenus)) return []

  // 하위 메뉴 상품 카테고리를 전부 합친다
  if (menuIndex > 0) {
    // 최하위 카테고리 인지 확인
    const usedMenuIndex = getSelectedMenuIndex(lastDepthMenu)
    categories = categories.concat(lastDepthMenu.displayMenus[usedMenuIndex].productCategories)
  } else {
    lastDepthMenu.displayMenus.forEach(menu => {
      if (!menu.productCategories) return
      categories = categories.concat(menu.productCategories)
    })
  }
  return ArrayUtils.getUniqueSet(categories)
}
